import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X, Plus, Minus, Send } from 'lucide-react';
import { SITE_INFO } from '../data';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setServicesDropdownOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Forside', path: '/' },
    { 
      name: 'Ydelser', 
      path: '/ydelser/tagarbejde',
      hasDropdown: true,
      subItems: [
        { name: 'Tagarbejde & Tagrenovering', path: '/ydelser/tagarbejde' },
        { name: 'Døre & Vinduer', path: '/ydelser/doere-og-vinduer' },
        { name: 'Specialløsninger', path: '/ydelser/specialloesninger' },
        { name: 'Træterrasser & Uderum', path: '/ydelser/traeterrasser' },
        { name: 'Renovering & Ombygning', path: '/ydelser/renovering-ombygning' },
        { name: 'Køkken & Bad', path: '/ydelser/koekken-og-bad' },
        { name: 'Fundament- og murerarbejde', path: '/ydelser/fundament-og-murerarbejde' },
        { name: 'Nybyg', path: '/ydelser/nybyg' }
      ]
    },
    { name: 'Projektudvikling', path: '/projektudvikling' },
    { name: 'Projekter', path: '/projekter' },
    { name: 'Galleri', path: '/galleri' },
    { name: 'Om os', path: '/om-os' },
    { name: 'Ledige stillinger', path: '/ledige-stillinger' },
    { name: 'Kontakt', path: '/kontakt' }
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header className="sticky top-0 z-40 bg-white text-stone-900 shadow-sm border-b border-stone-200">
      {/* Main Navbar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-20 sm:h-24 flex items-center justify-between">
        {/* Brand Logo */}
        <Link to="/" className="flex items-center group transition-transform duration-300 hover:scale-105 origin-left">
          <img
            src="https://assets.auxodev.dk/assets/58b18d64-1cfa-413a-b5ba-90838c1f6d2c/g28-nyt-logo-rez.png"
            alt="G28 Entreprise | Tømrer | Murer"
            className="h-16 sm:h-20 w-auto object-contain max-h-[85%] py-[5px]"
          />
        </Link>

        {/* Desktop Navigation - Hidden under 1200px */}
        <nav className="hidden min-[1200px]:flex items-center gap-3.5 min-[1250px]:gap-5 min-[1350px]:gap-6 text-xs font-semibold uppercase tracking-wider">
          {navLinks.map((link) => (
            <div 
              key={link.path} 
              className="relative py-2"
              onMouseEnter={() => link.hasDropdown && setServicesDropdownOpen(true)}
              onMouseLeave={() => link.hasDropdown && setServicesDropdownOpen(false)}
            >
              <Link
                to={link.path}
                className={`flex items-center gap-1.5 transition-colors py-1 ${
                  isActive(link.path)
                    ? 'text-amber-900 font-black'
                    : 'text-stone-800 hover:text-amber-700'
                }`}
              >
                <span>{link.name}</span>
                {link.hasDropdown && (
                  <ChevronDown className={`w-3.5 h-3.5 text-stone-500 transition-transform ${servicesDropdownOpen ? 'rotate-180 text-amber-700' : ''}`} aria-hidden="true" />
                )}
              </Link>

              {/* Dropdown Menu */}
              {link.hasDropdown && servicesDropdownOpen && (
                <div className="absolute top-full left-0 w-64 bg-white border border-stone-200 shadow-xl py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
                  {link.subItems.map((sub) => (
                    <Link
                      key={sub.path}
                      to={sub.path}
                      className={`block px-4 py-2.5 text-xs font-medium normal-case tracking-normal transition-colors hover:bg-stone-100 hover:text-stone-950 ${
                        location.pathname === sub.path ? 'text-amber-900 bg-stone-100 font-black' : 'text-stone-700'
                      }`}
                    >
                      {sub.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Mobile Burger Menu Button - Displayed strictly under 1200px */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="min-[1200px]:hidden p-2.5 text-stone-800 hover:text-amber-800 transition-colors cursor-pointer"
          aria-label={mobileMenuOpen ? 'Luk menu' : 'Åbn menu'}
        >
          {mobileMenuOpen ? (
            <X className="w-6 h-6" aria-hidden="true" />
          ) : (
            <Menu className="w-6 h-6" aria-hidden="true" />
          )}
        </button>
      </div>

      {/* Mobile Drawer Navigation (under 1200px) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden bg-white border-t border-stone-200 px-4 pt-4 pb-6 space-y-3 animate-in slide-in-from-top duration-200 shadow-xl">
          {navLinks.map((link) => (
            <div key={link.path} className="border-b border-stone-100 pb-2">
              {link.hasDropdown ? (
                <div>
                  <div className="flex items-center justify-between">
                    <Link
                      to={link.path}
                      className={`text-sm font-semibold uppercase tracking-wider py-2 ${
                        isActive(link.path) ? 'text-amber-900 font-black' : 'text-stone-800'
                      }`}
                    >
                      {link.name}
                    </Link>
                    <button
                      onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                      className="p-2 text-stone-500 hover:text-amber-800 cursor-pointer"
                    >
                      {mobileServicesOpen ? (
                        <Minus className="w-4 h-4" aria-hidden="true" />
                      ) : (
                        <Plus className="w-4 h-4" aria-hidden="true" />
                      )}
                    </button>
                  </div>
                  {mobileServicesOpen && (
                    <div className="pl-4 py-2 space-y-2 bg-stone-50 my-1 border-l-2 border-amber-700">
                      {link.subItems.map((sub) => (
                        <Link
                          key={sub.path}
                          to={sub.path}
                          className={`block py-1.5 text-sm normal-case tracking-normal transition-colors ${
                            location.pathname === sub.path ? 'text-amber-900 font-black' : 'text-stone-700 hover:text-stone-950'
                          }`}
                        >
                          {sub.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to={link.path}
                  className={`block text-sm font-semibold uppercase tracking-wider py-2 ${
                    isActive(link.path) ? 'text-amber-900 font-black' : 'text-stone-800 hover:text-amber-800'
                  }`}
                >
                  {link.name}
                </Link>
              )}
            </div>
          ))}

          {/* Mobile Direct Contact CTA */}
          <div className="pt-2">
            <Link
              to="/kontakt"
              className="w-full inline-flex items-center justify-center gap-2 bg-amber-700 hover:bg-amber-800 text-white font-heading font-bold text-base py-3 shadow-md"
            >
              <Send className="w-4 h-4" aria-hidden="true" />
              <span>Indhent Gratis Tilbud</span>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
